import { ContractActionsModal } from '@/components/features/agreements/listing/modals/ContractActionsModal'
import { CremationsCmp } from '@/components/features/agreements/listing/CremationsCmp'
import { ParcelsCmp } from '@/components/features/agreements/listing/ParcelsCmp'
import { NavHeader } from '@/components/features/shared/NavHeader'
import { Button, IconButton } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { UpdateAgreementData } from '@/domain/agreements/agreement'
import { ServiceType } from '@/domain/offerings/offering'
import { useAccounts } from '@/hooks/api/useAccounts'
import { useAgreement, useDeleteAgreement, useUpdateAgreement } from '@/hooks/api/useAgreements'
import { assuredPeopleKeys, useAssuredPeopleByContract } from '@/hooks/api/useAssuredPeople'
import { useCollectors } from '@/hooks/api/useCollectors'
import { deathDataKeys } from '@/hooks/api/useDeathData'
import { useOffering } from '@/hooks/api/useOfferings'
import { useParcelByContract } from '@/hooks/api/useParcels'
import { useSubscriptions } from '@/hooks/api/useSubscriptions'
import { useToast } from '@/hooks/use-toast'
import { Paths } from '@/routes/paths'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Power, RefreshCw, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { AssuredCmp } from '../../components/features/agreements/listing/AssuredCmp'

interface SingleAgreementPageProps {
  contractId: string
}

export const SingleAgreementPage = ({ contractId }: SingleAgreementPageProps) => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [showActionsModal, setShowActionsModal] = useState(false)

  const { data: agreement, isLoading: isLoadingAgreement, error: errorAgreement } = useAgreement(contractId)
  const deleteMutation = useDeleteAgreement()
  const updateMutation = useUpdateAgreement()
  const { data: offering, isLoading: isLoadingOffering, error: errorOffering } = useOffering(agreement?.offeringId ?? '')
  const serviceType = offering?.serviceType

  const { data: subscriptions } = useSubscriptions(contractId, {
    enabled: serviceType === ServiceType.ABONOS,
  })
  const { data: parcel } = useParcelByContract({ contractId, enabled: serviceType === ServiceType.PARCELAS })
  const { data: assuredPeople } = useAssuredPeopleByContract(contractId)

  const { data: collectors } = useCollectors()

  const { data: userAccounts, isLoading: isLoadingAccounts } = useAccounts(
    { userId: agreement?.userId },
    { enabled: !!agreement?.userId }
  )
  const totalBalance = userAccounts?.data?.reduce((sum, acc) => sum + acc.currentBalance, 0) || 0
  const contractAccount = userAccounts?.data?.find(acc => acc.contractId === contractId) || undefined

  const [isEditing, setIsEditing] = useState(false)

  const isLoading = isLoadingOffering || isLoadingAgreement || isLoadingAccounts;
  const error = errorOffering || errorAgreement;

  useEffect(() => {
    if (agreement?.mainContractId && agreement.mainContractId !== contractId) {
      navigate({ to: `${Paths.Management.Contracts}/${agreement.mainContractId}`, replace: true })
    }
  }, [agreement, contractId, navigate])

  useEffect(() => {
    if (error) {
      console.log(error)
      toast({
        title: 'Error',
        description: 'No se pudo cargar el contrato',
        variant: 'destructive'
      })
      navigate({ to: `${Paths.ManagementDashboard}` })
    }
  }, [error, navigate, toast])

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['agreements', contractId] })
    queryClient.invalidateQueries({ queryKey: ['accounts', contractId] })
    queryClient.invalidateQueries({ queryKey: ['invoices', contractId] })
    queryClient.invalidateQueries({ queryKey: ['subscriptions', contractId] })
    queryClient.invalidateQueries({ queryKey: ['redemptions'] })
    queryClient.invalidateQueries({ queryKey: ['assuredPeople'] })

    const mainLayout = document.getElementById('mainLayout')
    if (mainLayout?.classList.contains('overflow-auto')) {
      mainLayout.classList.remove('overflow-auto')
    }

    return () => {
      if (mainLayout && !mainLayout.classList.contains('overflow-auto')) {
        mainLayout.classList.add('overflow-auto')
      }
    }
  }, [])

  const handleDelete = async () => {
    if (confirm('¿Estás seguro de eliminar esta contratación?')) {
      await deleteMutation.mutateAsync(contractId)
      navigate({ to: `${Paths.Management.Contracts}` })
    }
  }

  const handleSave = async (changes: UpdateAgreementData) => {
    await updateMutation.mutateAsync({ id: contractId, data: changes })
    queryClient.invalidateQueries({ queryKey: assuredPeopleKeys.byContract(contractId) })
    queryClient.invalidateQueries({ queryKey: deathDataKeys.byContract(contractId) })
    queryClient.invalidateQueries({ queryKey: ['parcels', 'contract', contractId] })
    queryClient.invalidateQueries({ queryKey: ['parcels', 'detail', contractId] })
    setIsEditing(false)
  }

  const handleRefresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['collectors'] }),
      queryClient.invalidateQueries({ queryKey: ['agreements'] }),
      queryClient.invalidateQueries({ queryKey: ['agreement-payments'] }),
      queryClient.invalidateQueries({ queryKey: ['agreement-invoices'] }),
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] }),
      queryClient.invalidateQueries({ queryKey: ['deathData'] }),
      queryClient.invalidateQueries({ queryKey: ['parcels'] }),
      queryClient.invalidateQueries({ queryKey: ['accounts'] }),
      queryClient.invalidateQueries({ queryKey: ['offerings'] }),
      queryClient.invalidateQueries({ queryKey: ['redemptions'] })
    ])

    toast({
      title: 'Datos actualizados',
      description: 'Se han recargado los datos del contrato',
    })
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 space-y-4">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-1/2 mt-2" />
            </CardHeader>
            <CardContent className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-8 w-full" />
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-3/4" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (!agreement) {
    return (
      <div className="container mx-auto py-8 text-center">
        <p>No se encontró el contrato solicitado</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate({ to: `${Paths.Management.Contracts}` })}>
          Volver a la lista de contratos
        </Button>
      </div>
    )
  }

  const commonProps = {
    agreement,
    offering,
    contractAccount,
    userAccounts: userAccounts?.data || [],
    totalBalance,
    isLoadingAccounts,
    onViewAccountMovements: () => contractAccount && navigate({ to: `${Paths.Management.Accounts}/${contractAccount.id}` }),
    isEditing,
    isSaving: updateMutation.isPending,
    onSave: handleSave,
    onCancel: () => setIsEditing(false),
    onEdit: () => setIsEditing(true),
    collectors,
    sellers: collectors,
  }

  const renderContent = () => {
    switch (serviceType) {
      case ServiceType.ABONOS:
        return <AssuredCmp {...commonProps} />
      case ServiceType.PARCELAS:
        return <ParcelsCmp {...commonProps} />
      case ServiceType.CREMACIONES:
        return <CremationsCmp {...commonProps} />
      default:
        return (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Tipo de servicio no reconocido</p>
          </div>
        )
    }
  }

  return (
    <div className="w-full h-[calc(100vh-2rem)] flex flex-col space-y-6 xl:px-4 overflow-hidden py-4">
      <NavHeader
        actions={
          <>
            {!isEditing && (
              <div className="flex items-center gap-2">
                <IconButton
                  variant="default"
                  title="Refrescar datos"
                  onClick={handleRefresh}
                  disabled={updateMutation.isPending}
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${updateMutation.isPending ? 'animate-spin' : ''}`} />
                </IconButton>
                <IconButton
                  variant="default"
                  title="Acciones del contrato"
                  onClick={() => setShowActionsModal(true)}
                  disabled={updateMutation.isPending || !serviceType}
                >
                  <Power className="h-3.5 w-3.5" />
                </IconButton>
                <IconButton
                  variant="danger"
                  title="Eliminar contrato"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </IconButton>
              </div>
            )}
          </>
        }
      >
        <h1 className="text-2xl font-bold tracking-tight">
          Detalles de la Contratación
        </h1>
        <p className="text-muted-foreground">
          Número de contrato: {agreement.contractNumber}
        </p>
      </NavHeader>
      <ContractActionsModal
        agreement={agreement}
        isOpen={showActionsModal}
        onClose={() => setShowActionsModal(false)}
        serviceType={serviceType}
        subscriptionId={subscriptions?.[0]?.subscriptionId}
        parcelId={parcel?.id}
        parcel={parcel || undefined}
        people={assuredPeople}
      />
      <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar pb-24">
        {renderContent()}
      </div>
    </div>
  )
}
